import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LV = {
  city: 'Pilsēta', municipality: 'Novads', parish: 'Pagasts',
  district: 'Rajons', village: 'Ciems', region: 'Reģions',
};

export default function LocationPicker({ value, onSelect, placeholder, testid = 'location-picker' }) {
  const { language } = useLanguage();
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(debounceRef.current);
    if (v.trim().length < 2) { setResults([]); setOpen(false); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/locations`, { params: { q: v.trim(), limit: 8 } });
        setResults(data.locations || []);
        setOpen(true);
      } catch (err) { setResults([]); } finally { setLoading(false); }
    }, 250);
  };

  const pick = (loc) => {
    setQuery(loc.name);
    setOpen(false);
    onSelect({ id: loc.id, name: loc.name, slug: loc.slug, region_slug: loc.region_slug, lat: loc.lat, lng: loc.lng, type: loc.type });
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={query} onChange={handleChange} onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder || (language === 'lv' ? 'Meklē pilsētu vai ciemu...' : 'Search city or village...')}
          className="pl-9" data-testid={`${testid}-input`} />
        {loading && <Loader2 className="w-4 h-4 text-[#D97736] absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#E5E4E0] rounded-lg shadow-lg max-h-64 overflow-auto" data-testid={`${testid}-results`}>
          {results.map((loc) => (
            <button key={loc.id} type="button" onClick={() => pick(loc)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F3F2EE] transition-colors"
              data-testid={`${testid}-option-${loc.slug}`}>
              <MapPin className="w-4 h-4 text-[#D97736] shrink-0" />
              <span className="text-sm text-[#121212]">{loc.name}</span>
              <span className="text-xs text-[#9CA3AF] ml-auto">{TYPE_LV[loc.type] || loc.type}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
